import { css } from '@emotion/react'
import { Button, Cell, DropdownItem, Grid, Heading, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { FieldStatusEnum, Form, FormRenderProps, ListFieldStatus } from 'components/form'
import { HLabel } from 'components/HLabel'
import { TextStatus } from 'components/text/TextStatus'
import { FormApi } from 'final-form'
import { useState } from 'react'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { EditableRow } from 'view/atendimentos/detail/components/EditableList'
import { Observacao } from 'view/atendimentos/detail/components/Observacao'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'
import { removeWithOptionalConfirm } from 'view/retificacao-atendimento/removeWithOptionalConfirm'

import { MotivoConsultaFieldModel } from './MotivoConsultaField'

const path = metaPath<MotivoConsultaFieldModel>()

interface MotivoConsultaRowProps {
  model: MotivoConsultaFieldModel
  onChange(values: MotivoConsultaFieldModel): void
  onRemove(itemToRemove: MotivoConsultaFieldModel): void
  onReset?(itemToReset: MotivoConsultaFieldModel): void
  status?: FieldStatusEnum
  controlModifiedStatus?: boolean
}

const validator = createValidator<MotivoConsultaFieldModel>({
  observacao: [maxLength(200)],
})

export function MotivoConsultaRow(props: Readonly<MotivoConsultaRowProps>) {
  const { model, onChange, onRemove, onReset, status, controlModifiedStatus = false } = props

  const [editing, setEditing] = useState(false)

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)

  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleReset = () => onReset?.(model)

  const handleRemove = () =>
    removeWithOptionalConfirm({
      shouldAskConfirmation: controlModifiedStatus,
      title: 'Deseja excluir o Motivo da Consulta?',
      onRemove: () => onRemove(model),
    })

  const handleSubmit = (value: MotivoConsultaFieldModel, formApi: FormApi) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<MotivoConsultaFieldModel>) => (
    <VFlow vSpacing={0.5} style={{ padding: '1rem 0' }}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title='CIAP 2:'>{model.ciap.descricao}</HLabel>
      <ObservacaoFormGrid
        name={path.observacao}
        onCancel={toggleEditing}
        onSubmit={formRenderProps.handleSubmit}
        dataTestId='observacoesCiapRowSubjetivo'
      />
    </VFlow>
  )

  return (
    <EditableRow editing={editing} dataTestId='ciapRowSubjetivo'>
      {!editing && (
        <>
          <Grid style={styles.grid}>
            <Cell size={9} alignSelf='center'>
              <TextStatus status={status}>{`${model.ciap.descricao} - ${model.ciap.codigo}`}</TextStatus>
            </Cell>
            <Cell size={3} style={styles.actions}>
              {controlModifiedStatus && (
                <ListFieldStatus status={status} onReset={handleReset} onRemove={handleRemove}>
                  <DropdownItem onClick={toggleEditing}>
                    <HFlow alignItems='center' hSpacing={0.5}>
                      <Text variant='h4'>{editTooltipText}</Text>
                    </HFlow>
                  </DropdownItem>
                </ListFieldStatus>
              )}

              {!controlModifiedStatus && (
                <>
                  <Tooltip text={editTooltipText}>
                    <Button
                      size='small'
                      skin='ghost'
                      onClick={toggleEditing}
                      data-testid='adicionarObservacaoCiapSubjetivo'
                    >
                      <Icon icon='chatOutline' />
                    </Button>
                  </Tooltip>
                  <Tooltip text={removeTooltipText}>
                    <Button
                      size='small'
                      skin='ghost'
                      onClick={handleRemove}
                      disabled={isEditable}
                      data-testid='removerCiapSubjetivo'
                    >
                      <Icon icon='trashOutline' />
                    </Button>
                  </Tooltip>
                </>
              )}
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} status={status} />
        </>
      )}
      {editing && (
        <Form<MotivoConsultaFieldModel>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={validator}
          initialValuesEqual={initialValuesEqual}
        />
      )}
    </EditableRow>
  )
}

const initialValuesEqual = (motivo1: MotivoConsultaFieldModel, motivo2: MotivoConsultaFieldModel) =>
  motivo1._id === motivo2._id

const styles = {
  grid: css`
    margin: 0;
  `,
  actions: css`
    align-self: center;
    padding: 0.25rem 0;
    text-align: right;
  `,
}
